/**
 * SocialPulse.blog — homepage.
 *
 * Editorial landing surfacing the two-selector Place × Topic search
 * (our differentiator), category tiles, and hand-picked stories.
 *
 * Route: `/` when hostname is `socialpulse.blog` (see App.js
 * hostname-based routing). Also directly hittable at `/blog` on any
 * host for previews.
 *
 * Fonts: Lora (headline serif) + Inter (UI sans) + Caveat (handwritten
 * accents) — loaded in public/index.html.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen, Coins, Utensils, Plane, Leaf, Home as HomeIcon, Heart,
  Users, Search, MapPin, Compass, ChevronDown, Globe, ArrowRight,
} from "lucide-react";
import LocationSuggestBanner from "@/components/LocationSuggestBanner";
import ListenChip from "@/components/publisher/ListenChip";
import SharedBlogHeader from "@/components/blog/BlogHeader";
import FeaturedCarousel from "@/components/blog/FeaturedCarousel";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1/blog`;

// Icon name → Lucide component. Keeps the backend config data-only.
const ICON_MAP = {
  heart: Heart, "book-open": BookOpen, coins: Coins, utensils: Utensils,
  plane: Plane, leaf: Leaf, home: HomeIcon, users: Users,
  sparkles: Compass,
};

// Curated topic list surfaced in the "Choose a Topic" selector.
// Mirrors the backend CATEGORY_TILES but exposes a couple of extra
// topical entry points.
const TOPIC_OPTIONS = [
  { slug: "health",           label: "Health" },
  { slug: "education",        label: "Education" },
  { slug: "finance",          label: "Finance" },
  { slug: "food",             label: "Food" },
  { slug: "travel",           label: "Travel" },
  { slug: "nature",           label: "Nature" },
  { slug: "science",          label: "Science" },
  { slug: "weather",          label: "Weather" },
  { slug: "farmers",          label: "Farmers" },
  { slug: "alumni",           label: "Alumni" },
  { slug: "family-trees",     label: "Family Trees" },
  { slug: "memorials",        label: "Memorials" },
  { slug: "homes-real-estate", label: "Homes & Real Estate" },
  { slug: "family-heritage",  label: "Family & Heritage" },
  { slug: "community",        label: "Community" },
  { slug: "local-news",       label: "Local News" },
  { slug: "arts-culture",     label: "Arts & Culture" },
  { slug: "history",          label: "History" },
  { slug: "faith-spirituality", label: "Faith & Spirituality" },
  { slug: "technology",       label: "Technology" },
  { slug: "business",         label: "Business" },
];

// Popular places for the "Choose a Place" selector — cheap search
// endpoint on the existing locations collection would replace this
// with live suggestions in Phase 2.
const PLACE_OPTIONS = [
  { slug: "united-states/oregon/portland", label: "Portland, Oregon" },
  { slug: "united-states/california",      label: "California" },
  { slug: "india/kerala",                  label: "Kerala, India" },
  { slug: "india/kerala/kottayam",         label: "Kottayam, Kerala" },
  { slug: "japan/kyoto",                   label: "Kyoto, Japan" },
  { slug: "greece/santorini",              label: "Santorini, Greece" },
  { slug: "brazil/rio-de-janeiro",         label: "Rio de Janeiro, Brazil" },
  { slug: "peru/cusco",                    label: "Cusco, Peru" },
  { slug: "south-africa/cape-town",        label: "Cape Town, South Africa" },
  { slug: "denmark/copenhagen",            label: "Copenhagen, Denmark" },
  { slug: "kenya/nairobi",                 label: "Nairobi, Kenya" },
];

export default function BlogHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [locKey, setLocKey] = useState(0);   // bumped when banner accepts a city

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Read the saved home city so the server can return a
        // localized hero built around the reader's own place.
        let city = null;
        try {
          const raw = window.localStorage.getItem("sp_home_city")
                   || window.localStorage.getItem("np_home_city");
          if (raw) city = JSON.parse(raw);
        } catch { /* noop */ }
        const q = new URLSearchParams();
        if (city?.city)    q.set("near_city",    city.city);
        if (city?.state)   q.set("near_state",   city.state);
        if (city?.country) q.set("near_country", city.country);
        const url = `${BASE}/homepage${q.toString() ? `?${q}` : ""}`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`bad_status_${r.status}`);
        const d = await r.json();
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e.message);
      }
    })();
    return () => { alive = false; };
  }, [locKey]);

  // Refetch when the reader saves / changes their home city so the
  // hero re-personalizes without a full page reload.
  useEffect(() => {
    const onStorage = (e) => {
      if (!e || !e.key || e.key === "sp_home_city" || e.key === "np_home_city") {
        setLocKey((k) => k + 1);
      }
    };
    const onLocal = () => setLocKey((k) => k + 1);
    window.addEventListener("storage", onStorage);
    window.addEventListener("sp:home-city-changed", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sp:home-city-changed", onLocal);
    };
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{ background: "#FAFAF7", color: "#0F172A", fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="blog-home"
    >
      <SharedBlogHeader activeNav="" />

      <main className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Hero highlights={data?.hero?.highlights || []} onExplore={navigate} />
        <FeaturedCarousel picks={data?.editors_picks || []} />
        <ExploreYourWorld
          categories={data?.categories || []}
          categoriesSource={data?.categories_source}
          onNavigate={navigate}
        />
        <StoriesNearYou stories={data?.stories || []} onNavigate={navigate} loading={!data && !error} />
      </main>

      <BlogFooter />
    </div>
  );
}


// ─── Hero ────────────────────────────────────────────────────────

function Hero({ highlights, onExplore }) {
  const [place, setPlace] = useState("");
  const [topic, setTopic] = useState("");

  const explore = () => {
    if (!place && !topic) {
      onExplore("/blog/places");
      return;
    }
    if (place && topic) onExplore(`/place/${place}/topic/${topic}`);
    else if (place) onExplore(`/place/${place}`);
    else onExplore(`/blog/topics/${topic}`);
  };

  return (
    <section className="py-14 md:py-16 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center" data-testid="blog-hero">
      {/* Left column — headline + selectors */}
      <div className="max-w-2xl">
        <p className="text-[11px] tracking-[0.24em] text-slate-500 uppercase mb-6">A more connected world</p>
        <Link
          to="/blog/about-socialpulse-blog"
          className="group inline-block hover:opacity-90 transition-opacity"
          data-testid="blog-hero-headline-link"
          aria-label="Read our manifesto: SocialPulse and SocialPulse.blog — Giving Every Community a Voice"
        >
          <h1
            className="text-slate-950 group-hover:underline decoration-[#0F766E] decoration-2 underline-offset-[8px]"
            style={{
              fontFamily: "'Lora', serif",
              fontWeight: 600,
              fontSize: "clamp(1.9rem, 3.4vw, 2.9rem)",
              lineHeight: "1.08",
              letterSpacing: "-0.02em",
            }}
          >
            Every Place Has a Story.<br />
            Every Subject Has a Community.
          </h1>
          <span
            className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest group-hover:underline"
            style={{ color: "#0F766E" }}
            data-testid="blog-hero-read-more"
          >
            Read more <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </Link>
        <p className="mt-6 text-slate-600 text-lg max-w-lg leading-relaxed">
          Discover stories, ideas and local knowledge from communities around the world.
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
          <SelectorField
            testid="hero-place"
            label="Choose a Place"
            sublabel="City, country or region"
            icon={MapPin}
            value={place}
            onChange={setPlace}
            options={PLACE_OPTIONS}
            placeholder="City, country or region"
          />
          <SelectorField
            testid="hero-topic"
            label="Choose a Topic"
            sublabel="Any subject you're curious about"
            icon={BookOpen}
            value={topic}
            onChange={setTopic}
            options={TOPIC_OPTIONS}
            placeholder="Any subject you're curious about"
          />
        </div>
        <button
          onClick={explore}
          className="mt-4 w-full max-w-xl rounded-2xl py-3.5 text-white text-base font-semibold inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.005]"
          style={{ background: "#0F766E", boxShadow: "0 10px 24px -10px rgba(15,118,110,0.55)" }}
          data-testid="hero-explore-btn"
        >
          <Compass className="w-5 h-5" />
          Explore
        </button>
        <p className="mt-4 text-sm text-slate-400">Real people. Real places. A more connected world.</p>
      </div>

      {/* Right column — dotted world map with pinned story cards */}
      <div className="relative h-[440px] lg:h-[520px]" aria-hidden="true" data-testid="hero-map">
        <WorldMapBackdrop />
        {highlights.map((h, i) => (
          <MapStoryCard key={i} h={h} />
        ))}
        <HandwrittenNote style={{ top: "44%", left: "0%" }} rotate={-6}>
          Different places.<br />Brighter perspectives.
        </HandwrittenNote>
        <HandwrittenNote style={{ top: "-2%", right: "22%" }} rotate={-4}>
          Local stories.<br />Global connections.
        </HandwrittenNote>
        <HandwrittenNote style={{ bottom: "6%", right: "-4%" }} rotate={-6}>
          Stories<br />travel further<br />together.
        </HandwrittenNote>
      </div>
    </section>
  );
}


function SelectorField({ testid, label, sublabel, icon: Icon, value, onChange, options, placeholder }) {
  return (
    <label
      className="relative flex items-center gap-3 bg-white border rounded-2xl px-4 py-3 cursor-pointer hover:border-teal-600 transition-colors"
      style={{ borderColor: "rgba(15,23,42,0.10)" }}
      data-testid={testid}
    >
      <span
        className="w-9 h-9 rounded-full grid place-items-center shrink-0"
        style={{ background: "rgba(15,118,110,0.10)" }}
      >
        <Icon className="w-4.5 h-4.5" style={{ color: "#0F766E" }} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{label}</span>
        <span className="block text-xs text-slate-500 truncate">
          {options.find((o) => o.slug === value)?.label || sublabel}
        </span>
      </span>
      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 cursor-pointer"
        aria-label={label}
        data-testid={`${testid}-select`}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.slug} value={o.slug}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}


function WorldMapBackdrop() {
  // Dotted world map — pure CSS via a stylized SVG background. Uses
  // radial-gradient dots on a masked continent silhouette; the mask is
  // an inline SVG that ships without external assets.
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      <svg viewBox="0 0 900 460" className="w-full h-full" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="dots" x="0" y="0" width="9" height="9" patternUnits="userSpaceOnUse">
            <circle cx="4" cy="4" r="1.7" fill="#94a3b8" opacity="0.7" />
          </pattern>
        </defs>
        {/* Rough continent silhouettes */}
        <g fill="url(#dots)">
          <path d="M50,90 Q120,55 210,75 L275,155 L245,240 L145,255 L90,190 Z" />
          <path d="M210,270 L275,265 L285,365 L235,425 L200,385 Z" />
          <path d="M415,80 L505,90 L525,145 L475,175 L410,155 Z" />
          <path d="M435,180 L535,190 L545,320 L470,385 L425,320 L415,240 Z" />
          <path d="M540,80 L790,90 L825,180 L765,235 L640,235 L580,185 L540,130 Z" />
          <path d="M695,275 L805,295 L825,345 L765,375 L710,345 Z" />
          <path d="M780,375 L830,385 L820,425 L780,415 Z" />
        </g>
        {/* Connector arcs between hero pins */}
        <g stroke="#0F766E" strokeWidth="1.2" fill="none" opacity="0.4" strokeDasharray="2 5">
          <path d="M175,165 Q380,80 720,150" />
          <path d="M175,165 Q400,300 720,340" />
          <path d="M250,330 Q500,280 720,340" />
        </g>
        {/* Location pins scattered across continents so the map reads
            "live" even before real articles land in every place. */}
        <g>
          {[
            [190, 150, "#f59e0b"], [255, 285, "#f59e0b"],
            [470, 150, "#f59e0b"], [510, 320, "#10b981"],
            [640, 155, "#f59e0b"], [745, 175, "#f59e0b"],
            [770, 315, "#10b981"], [805, 400, "#f59e0b"],
          ].map(([cx, cy, color], i) => (
            <g key={i}>
              <circle cx={cx} cy={cy} r="7" fill={color} opacity="0.25" />
              <circle cx={cx} cy={cy} r="3.5" fill={color} />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}


function MapStoryCard({ h }) {
  const pos = h.position || {};
  return (
    <a
      href={h.href}
      className="absolute w-48 bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
      style={{ ...pos, boxShadow: "0 14px 36px -12px rgba(15,23,42,0.25)" }}
      data-testid="hero-map-card"
    >
      <div className="w-full h-24 overflow-hidden">
        <img src={h.image} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-3 pb-3.5">
        <p
          className="text-[15px] leading-snug text-slate-900"
          style={{ fontFamily: "'Lora', serif", fontWeight: 600, whiteSpace: "pre-line" }}
        >{h.title}</p>
        <p className="mt-1.5 text-[11px] text-slate-500 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" style={{ color: h.pin?.color || "#f59e0b" }} />
          {h.place}
        </p>
      </div>
    </a>
  );
}


function HandwrittenNote({ style, children, rotate = 0 }) {
  return (
    <span
      className="absolute text-[15px] leading-tight pointer-events-none"
      style={{
        ...style,
        transform: `rotate(${rotate}deg)`,
        fontFamily: "'Caveat', cursive",
        color: "#0F766E",
        opacity: 0.85,
      }}
    >
      {children}
    </span>
  );
}


// ─── Explore Your World (category tiles) ─────────────────────────

function ExploreYourWorld({ categories, categoriesSource, onNavigate }) {
  const isLocalized = categoriesSource === "localized";
  return (
    <section className="pt-6 pb-10" data-testid="blog-categories">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "clamp(1.75rem, 3vw, 2.5rem)", letterSpacing: "-0.02em" }}>
            Explore Your World
          </h2>
          {isLocalized && (
            <p className="mt-1 text-xs uppercase tracking-widest font-semibold" style={{ color: "#0F766E" }}>
              Popular near you first
            </p>
          )}
        </div>
        <p className="hidden sm:block text-slate-500 text-sm">
          Discover communities through the topics that matter most.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
        {categories.slice(0, 8).map((c) => {
          const Icon = ICON_MAP[c.icon] || Compass;
          const hasLocal = isLocalized && (c.local_count || 0) > 0;
          return (
            <button
              key={c.slug}
              onClick={() => onNavigate(`/blog/topics/${c.slug}`)}
              className="group bg-white rounded-2xl px-4 py-5 flex flex-col items-center gap-2.5 hover:shadow-md hover:-translate-y-0.5 transition-all border relative"
              style={{ borderColor: hasLocal ? `${c.color}55` : "rgba(15,23,42,0.06)" }}
              data-testid={`blog-category-${c.slug}`}
            >
              {hasLocal && (
                <span
                  className="absolute top-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                  style={{ background: c.color }}
                  data-testid={`blog-category-local-badge-${c.slug}`}
                >
                  {c.local_count}
                </span>
              )}
              <span
                className="w-11 h-11 rounded-full grid place-items-center transition-transform group-hover:scale-110"
                style={{ background: `${c.color}12` }}
              >
                <Icon className="w-6 h-6" style={{ color: c.color }} />
              </span>
              <span className="text-[15px] font-medium text-slate-800">{c.label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}


// ─── Stories Near You ────────────────────────────────────────────

function StoriesNearYou({ stories, onNavigate, loading }) {
  const lead = stories.find((s) => s.size === "lead");
  const compact = stories.filter((s) => s !== lead).slice(0, 3);
  return (
    <section className="pb-16" data-testid="blog-stories">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "clamp(1.75rem, 3vw, 2.5rem)", letterSpacing: "-0.02em" }}>
            Stories Near You
            <span className="text-slate-500 text-base font-normal ml-3" style={{ fontFamily: "Inter" }}>
              Local voices. Global ideas. Real stories from around you.
            </span>
          </h2>
        </div>
        <button
          onClick={() => onNavigate("/blog/stories")}
          className="inline-flex items-center gap-1 font-semibold"
          style={{ color: "#0F766E" }}
          data-testid="blog-stories-viewall"
        >
          View All Stories <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {loading && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="h-96 rounded-2xl bg-slate-100 animate-pulse" />
          <div className="grid gap-4">
            {[0,1,2].map((i) => <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        </div>
      )}

      {!loading && (
        <div className="grid lg:grid-cols-2 gap-6">
          {lead && <LeadStoryCard s={lead} />}
          <div className="grid gap-4">
            {compact.map((s, i) => <CompactStoryCard key={i} s={s} />)}
          </div>
        </div>
      )}
    </section>
  );
}


function LeadStoryCard({ s }) {
  const slug = extractBlogSlug(s.href);
  return (
    <a
      href={s.href}
      className="relative block rounded-2xl overflow-hidden group aspect-[4/5] sm:aspect-[16/11]"
      data-testid="blog-story-lead"
    >
      <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-[1.02]" loading="lazy" />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 20%, rgba(0,0,0,0.75) 100%)" }}
      />
      <div className="absolute top-4 left-4 flex flex-wrap items-center gap-2">
        <ChipLocation text={s.place} />
        <ChipCategory cat={s.category} />
      </div>
      {slug && (
        <div className="absolute top-4 right-4">
          <ListenChip slug={slug} testIdPrefix={`blog-home-lead-listen-${slug}`} />
        </div>
      )}
      <div className="absolute bottom-5 left-5 right-5 text-white">
        <h3
          className="text-2xl md:text-3xl mb-2 max-w-lg leading-tight"
          style={{ fontFamily: "'Lora', serif", fontWeight: 600, whiteSpace: "pre-line" }}
        >{s.title}</h3>
        <p className="text-sm text-white/90 max-w-md line-clamp-2">{s.excerpt}</p>
      </div>
    </a>
  );
}


function CompactStoryCard({ s }) {
  const slug = extractBlogSlug(s.href);
  return (
    <a
      href={s.href}
      className="relative block rounded-2xl overflow-hidden group aspect-[16/9]"
      data-testid="blog-story-compact"
    >
      <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-[1.02]" loading="lazy" />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.75) 100%)" }}
      />
      <div className="absolute top-3 left-3 flex flex-wrap items-center gap-2">
        <ChipLocation text={s.place} small />
        <ChipCategory cat={s.category} small />
      </div>
      {slug && (
        <div className="absolute top-3 right-3">
          <ListenChip slug={slug} testIdPrefix={`blog-home-compact-listen-${slug}`} />
        </div>
      )}
      <div className="absolute bottom-3 left-3 right-3 text-white">
        <h3
          className="text-lg md:text-xl mb-1 leading-tight"
          style={{ fontFamily: "'Lora', serif", fontWeight: 600, whiteSpace: "pre-line" }}
        >{s.title}</h3>
        <p className="text-xs text-white/90 line-clamp-2">{s.excerpt}</p>
      </div>
    </a>
  );
}

// Extract the article slug from an /blog/<slug> href so we can drive
// TTS lookups on story cards. Returns null for non-article links.
function extractBlogSlug(href) {
  if (!href || typeof href !== "string") return null;
  const m = href.match(/^\/blog\/([^/?#]+)(?:[/?#]|$)/);
  if (!m) return null;
  const slug = m[1];
  // These are section pages, not articles — no TTS to fetch.
  const reserved = new Set([
    "topics", "places", "news", "questions", "publications",
    "writers", "search", "auth", "about",
  ]);
  if (reserved.has(slug)) return null;
  return slug;
}


function ChipLocation({ text, small }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur ${small ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"} font-semibold text-slate-800`}
    >
      <MapPin className={small ? "w-2.5 h-2.5" : "w-3 h-3"} style={{ color: "#f59e0b" }} />
      {text}
    </span>
  );
}


function ChipCategory({ cat, small }) {
  const Icon = ICON_MAP[cat?.icon] || Compass;
  const color = cat?.color || "#0F766E";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full backdrop-blur ${small ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"} font-semibold`}
      style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
    >
      <Icon className={small ? "w-2.5 h-2.5" : "w-3 h-3"} />
      {cat?.label}
    </span>
  );
}


// ─── Footer ──────────────────────────────────────────────────────

function BlogFooter() {
  return (
    <footer
      className="mt-8 border-t"
      style={{ borderColor: "rgba(15,118,110,0.08)", background: "#fff" }}
      data-testid="blog-footer"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 grid gap-6 md:grid-cols-4 text-sm text-slate-600">
        <div>
          <p className="text-lg text-slate-900" style={{ fontFamily: "'Lora', serif", fontWeight: 700 }}>
            SocialPulse® <span style={{ color: "#0F766E" }}>Blog</span>
          </p>
          <p className="mt-2 text-slate-500 max-w-xs">
            A worldwide publishing and community network. Every place has a story. Every subject has a community.
          </p>
        </div>
        <div>
          <p className="font-semibold text-slate-900 mb-2">Explore</p>
          <ul className="space-y-1.5">
            <li><a href="/blog/places" className="hover:text-slate-950">Places</a></li>
            <li><a href="/blog/topics" className="hover:text-slate-950">Topics</a></li>
            <li><a href="/blog/questions" className="hover:text-slate-950">Questions</a></li>
            <li><a href="/blog/publications" className="hover:text-slate-950">Publications</a></li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900 mb-2">Network</p>
          <ul className="space-y-1.5">
            <li><a href="https://socialpulse.com" className="hover:text-slate-950">SocialPulse.com</a></li>
            <li><a href="https://chats.socialpulse.blog" className="hover:text-slate-950">Chats</a></li>
            <li><a href="https://news.socialpulse.blog" className="hover:text-slate-950">News</a></li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-slate-900 mb-2">Get Started</p>
          <ul className="space-y-1.5">
            <li><a href="/publisher/write" className="hover:text-slate-950">Write a story</a></li>
            <li><a href="/auth" className="hover:text-slate-950">Sign in</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t py-4 text-center text-xs text-slate-400" style={{ borderColor: "rgba(15,23,42,0.05)" }}>
        © {new Date().getFullYear()} SocialPulse — Every Place Has a Story. Every Subject Has a Community.
      </div>
    </footer>
  );
}
